import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
} from '@mui/material';
import useAPI from '../../hooks/useAPI';
import { useDarkMode } from '../../hooks/useDarkMode';
import OtpInput from '../../components/otp-input';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (response?: any) => void;
  isSwitching?: boolean;
  isAlreadySetUp?: boolean;
  enableCompanyMfaAfterSetup?: boolean;
  email?: string; // Optional email for login-time setup (no auth required)
}

const TotpSetupDialog: React.FC<Props> = ({ open, onClose, onSuccess, isSwitching = false, isAlreadySetUp = false, enableCompanyMfaAfterSetup = false, email }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const API = useAPI();
  const [loading, setLoading] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipQrCode, setSkipQrCode] = useState(false);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  useEffect(() => {
    if (open) {
      if (isSwitching && isAlreadySetUp) {
        // When switching and already set up, skip QR code and go straight to verification
        setSkipQrCode(true);
        setLoading(false);
      } else {
        // New setup - generate QR code
        setSkipQrCode(false);
        setupTotp();
      }
    } else {
      setQrCodeDataUrl(null);
      setVerificationCode('');
      setError(null);
      setSkipQrCode(false);
      setVerificationAttempted(false);
    }
  }, [open, isSwitching, isAlreadySetUp]);

  const setupTotp = async () => {
    try {
      setLoading(true);
      setError(null);
      // If email is provided, use login-time endpoint (no auth required)
      const response = await API.setupTotp(email);
      setQrCodeDataUrl(response.qrCodeDataUrl);
    } catch (err: any) {
      console.error('[TOTP Setup] Error:', err);
      const errorMessage = err?.response?.data?.message || err?.message || t('sentences:pages.mfa_setup.totp_setup_error') || 'Failed to set up TOTP';
      setError(errorMessage);
      // If it's a 404 or route not found, provide more helpful error
      if (err?.response?.status === 404 || errorMessage.includes('Route not found')) {
        setError('Unable to set up TOTP. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    // Remove all frontend validation - let backend handle it
    setVerificationAttempted(true);
    try {
      setVerifying(true);
      setError(null);
      
      // verifyTotp already handles switching (sets preferredMethod to TOTP)
      // If email is provided, use login-time endpoint (no auth required)
      const response = await API.verifyTotp(verificationCode, email);
      
      // If this is login-time setup, the response includes accessToken and user
      // We should handle that in the parent component
      if (email && response.accessToken) {
        // Login-time setup completed - parent should handle the token
      }
      
      // If this is admin/owner setting up their method to enable company MFA, enable it now
      if (enableCompanyMfaAfterSetup) {
        try {
          await API.put(`companies/updateMfa`, { mfa: true });
        } catch (err: any) {
          console.error('[TOTP Setup] Error enabling company MFA:', err);
          setError(err?.response?.data?.message || 'Failed to enable company MFA');
          setVerifying(false);
          return;
        }
      }
      
      // Pass response to onSuccess callback (for login-time setup)
      onSuccess(response);
      onClose();
    } catch (err: any) {
      console.error('[TOTP Setup] Verification error:', err);
      setError(err?.response?.data?.message || t('sentences:pages.mfa_setup.totp_verify_error') || 'Invalid verification code');
      setVerificationCode('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isSwitching 
          ? (t('sentences:pages.mfa_setup.totp_switch_title') || 'Switch to Authenticator App')
          : (t('sentences:pages.mfa_setup.totp_title') || 'Set Up Authenticator App')}
      </DialogTitle>
      <DialogContent>
        {loading && !skipQrCode ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && verificationAttempted && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }} 
                onClose={() => {
                  setError(null);
                  setVerificationAttempted(false);
                }}
              >
                {error}
              </Alert>
            )}

            {!skipQrCode && (
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                {t('sentences:pages.mfa_setup.totp_description') || 
                  'Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)'}
              </Typography>
            )}

            {isSwitching && isAlreadySetUp && (
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                {t('sentences:pages.mfa_setup.totp_switch_description') || 
                  'Please verify your authenticator app code to switch to this method'}
              </Typography>
            )}

            {qrCodeDataUrl && !skipQrCode && (
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  backgroundColor: '#ffffff'
                }}
              >
                <img 
                  src={qrCodeDataUrl} 
                  alt="QR Code" 
                  style={{ 
                    width: '256px', 
                    height: '256px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                  }} 
                />
                <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                  {t('sentences:pages.mfa_setup.totp_scan_instruction') || 
                    'Scan this QR code with your authenticator app'}
                </Typography>
                <Box 
                  sx={{ 
                    mt: 1.5, 
                    p: 1.5, 
                    backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5', 
                    borderRadius: 1,
                    border: isDarkMode ? '1px solid #404040' : '1px solid #e0e0e0'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      textAlign: 'center', 
                      color: isDarkMode ? '#b0b0b0' : '#666666', 
                      fontSize: '11px',
                      lineHeight: 1.5,
                      display: 'block'
                    }}
                  >
                    {t('sentences:pages.mfa_setup.totp_logo_note') || 
                      'Note: Some apps may ask you to search for "Valital" logo - this is normal'}
                  </Typography>
                </Box>
              </Paper>
            )}

            <Box>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                {t('sentences:pages.mfa_setup.totp_enter_code') || 'Enter the 6-digit code from your authenticator app'}
              </Typography>
              <OtpInput
                length={6}
                value={verificationCode}
                onChange={(value) => {
                  setVerificationCode(value);
                  // Clear error and reset verification attempt when user types
                  if (error) {
                    setError(null);
                  }
                  setVerificationAttempted(false);
                }}
                disabled={verifying}
                error={false}
                onComplete={() => {
                  // Don't auto-submit - user must click VERIFY button
                  // Just clear any previous errors
                  if (error) {
                    setError(null);
                  }
                  setVerificationAttempted(false);
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={verifying}>
          {t('verbs.cancel')}
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={verifying || loading}
          sx={{
            backgroundColor: '#1083c6',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            height: '55px',
            minWidth: '120px',
            '&:hover': {
              backgroundColor: '#006AAD',
            },
            '&:disabled': {
              opacity: 0.3,
              backgroundColor: '#1083c6',
            }
          }}
        >
          {verifying ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              {t('verbs.verifying') || 'Verifying...'}
            </>
          ) : (
            t('verbs.verify')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TotpSetupDialog;

